import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { useApi } from '../api/useApi';
import { ApiError, type PackageDocResponse } from '../api/client';
import type { PackageOperations } from '@almanac/contracts';
import { BriefingMarkdown } from './BriefingMarkdown';
import { packageGithubUrl, parseBriefing, parsePackageDoc } from '../lib/briefing-md';

/** Frontmatter fields shown as chips in the panel header, in this order. */
const CHIP_FIELDS = ['status', 'scale', 'area', 'steward'] as const;

/**
 * Slide-over viewer for one feature-package doc from almanac-wiki, opened
 * from a package link in the briefing without leaving the page. Renders the
 * doc's frontmatter as header chips and its body with the briefing's section
 * treatment; nested package links re-target the same panel.
 */
export function PackagePanel({
  slug,
  onOpenPackage,
  onClose,
}: {
  slug: string;
  onOpenPackage: (slug: string) => void;
  onClose: () => void;
}) {
  const api = useApi();
  const [doc, setDoc] = useState<PackageDocResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    setDoc(null);
    setError(null);
    const load = async () => {
      try {
        const response = await api.getBriefingPackage(slug);
        if (!stale) setDoc(response);
      } catch (err: unknown) {
        if (stale) return;
        if (err instanceof ApiError && err.status === 404) {
          setError('This package doc does not exist in the wiki.');
        } else {
          setError('Failed to load the package doc.');
        }
      }
    };
    void load();
    return () => {
      stale = true;
    };
  }, [api, slug]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Modal focus contract for `aria-modal`: focus moves into the panel on
  // open, Tab cycles within it, and focus returns to the opener on close.
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus();
    return () => opener?.focus();
  }, []);

  const trapFocus = (event: ReactKeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = dialog.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  const parsed = useMemo(() => {
    if (!doc) return null;
    const { fields, body } = parsePackageDoc(doc.body_md);
    return { fields, content: parseBriefing(body) };
  }, [doc]);

  let content: ReactNode;
  if (error) {
    content = <p className="text-sm text-muted-foreground">{error}</p>;
  } else if (!parsed) {
    content = <p className="text-sm text-muted-foreground">Loading…</p>;
  } else {
    const title = parsed.fields.title ?? parsed.content.title ?? slug;
    content = (
      <div className="space-y-8">
        <header className="space-y-2">
          <h2 className="font-display text-xl font-semibold tracking-tight text-foreground">
            {title}
          </h2>
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            {CHIP_FIELDS.map((field) => {
              const value = parsed.fields[field];
              if (!value) return null;
              return (
                <span
                  key={field}
                  className="rounded-full border border-border bg-muted/50 px-2 py-0.5 text-muted-foreground"
                >
                  {field === 'status' || field === 'scale' ? value : `${field}: ${value}`}
                </span>
              );
            })}
            <a
              href={packageGithubUrl(slug)}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="rounded-full border border-border px-2 py-0.5 font-medium text-primary hover:bg-muted/50"
            >
              Open on GitHub ↗
            </a>
          </div>
        </header>
        <PackageOperationsPanel key={slug} slug={slug} />
        <p className="text-xs text-muted-foreground">
          Package narrative below is a saved wiki snapshot. Check live operations above for current
          PR evidence.
        </p>
        {parsed.content.preamble ? (
          <BriefingMarkdown body={parsed.content.preamble} onOpenPackage={onOpenPackage} />
        ) : null}
        {parsed.content.sections.map((section, index) => (
          <section key={`${index}-${section.title}`} aria-label={section.title}>
            <div className="mb-2 flex items-center gap-2.5">
              <span aria-hidden className="h-3.5 w-[3px] flex-none rounded-full bg-primary" />
              <h3 className="font-display text-base font-semibold text-foreground">
                {section.title}
              </h3>
              <span aria-hidden className="h-px flex-1 bg-border" />
            </div>
            <BriefingMarkdown body={section.body} onOpenPackage={onOpenPackage} />
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close package viewer"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      {/* Rendered non-modally (`open`, not showModal) so the page behind
          stays scrollable; the modal focus contract is implemented by the
          ref/tabIndex/keydown trio above. */}
      <dialog
        ref={dialogRef}
        open
        tabIndex={-1}
        onKeyDown={trapFocus}
        aria-modal="true"
        aria-label={`Package ${slug}`}
        className="absolute inset-y-0 left-auto right-0 m-0 flex h-full max-h-none w-full max-w-xl flex-col border-y-0 border-l border-r-0 border-border bg-background p-0 text-foreground shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <span className="font-mono text-xs text-muted-foreground">{slug}.md</span>
          <button
            type="button"
            aria-label="Close"
            className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">{content}</div>
      </dialog>
    </div>
  );
}

/** Keep package requests isolated and expire evidence even if a refresh hangs. */
function PackageOperationsPanel({ slug }: { slug: string }) {
  const api = useApi();
  const [operations, setOperations] = useState<PackageOperations | null>(null);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    let disposed = false;
    let inFlight = false;
    const load = async () => {
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      setLoading(true);
      try {
        const result = await api.getPackageOperations(slug);
        if (!disposed) setOperations(result);
      } catch {
        if (!disposed)
          setOperations({
            status: 'unavailable',
            fetched_at: new Date().toISOString(),
            snapshot: null,
            reason: 'Live workflow information could not be refreshed.',
            references: [],
          });
      } finally {
        inFlight = false;
        if (!disposed) setLoading(false);
      }
    };
    void load();
    const interval = window.setInterval(() => void load(), 30_000);
    const visibility = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => {
      disposed = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [api, slug, refresh]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => window.clearInterval(interval);
  }, []);

  const observed = operations?.snapshot;
  const expired =
    observed &&
    [observed.observed_at, ...observed.pull_requests.map((pr) => pr.observed_at)].some(
      (value) => now - Date.parse(value) > 120_000 || Date.parse(value) > now + 30_000,
    );
  let display = operations;
  if (expired && operations) {
    display = { ...operations, status: 'stale' as const };
  }
  return (
    <PackageOperationsView
      operations={display}
      loading={loading}
      onRefresh={() => setRefresh((value) => value + 1)}
    />
  );
}

function PackageOperationsView({
  operations,
  loading,
  onRefresh,
}: {
  operations: PackageOperations | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  const statusClass = {
    current: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    partial: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300',
    stale: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300',
    unavailable: 'border-border bg-muted/50 text-muted-foreground',
  };
  const statusLabel = operations?.status ?? 'loading';
  const snapshot = operations?.snapshot;

  return (
    <section className="rounded-lg border border-border bg-card p-3" aria-label="Live operations">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Live operations</h3>
          <span
            className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${statusClass[operations?.status ?? 'unavailable']}`}
          >
            {statusLabel}
          </span>
        </div>
        <button
          type="button"
          className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
          disabled={loading}
          onClick={onRefresh}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      {!operations ? (
        <p className="mt-2 text-xs text-muted-foreground">Loading current-head evidence…</p>
      ) : null}
      {operations?.reason ? (
        <p className="mt-2 text-xs text-muted-foreground">{operations.reason}</p>
      ) : null}
      {operations?.fetched_at ? (
        <p className="mt-2 text-xs text-muted-foreground">
          View refreshed {new Date(operations.fetched_at).toLocaleString()}.
        </p>
      ) : null}
      {snapshot ? (
        <div className="mt-3 space-y-2 text-xs">
          <p className="text-muted-foreground">
            Source observed {new Date(snapshot.observed_at).toLocaleString()}.
          </p>
          {snapshot.actions.map((action) => (
            <div
              key={action.action_id}
              className="rounded border border-border/70 p-2 text-muted-foreground"
            >
              <span className="font-medium text-foreground">{action.work_kind}</span> ·{' '}
              {action.status} · {action.ownership_mode}
              {action.next_action ? <p className="mt-1">Next: {action.next_action}</p> : null}
              <RunLink url={action.run_url} />
            </div>
          ))}
          {snapshot.pull_requests.map((pr) => (
            <div
              key={`${pr.repository}#${pr.number}`}
              className="rounded border border-border/70 p-2 text-muted-foreground"
            >
              <PullRequestLink repository={pr.repository} number={pr.number} url={pr.url} /> ·{' '}
              {pr.state} · checks {pr.checks} · Greptile {pr.greptile} · human review {pr.review} ·
              deploy {pr.deployment}
              <p className="mt-1">
                {pr.title} · head {pr.head_sha.slice(0, 8)} · observed{' '}
                {new Date(pr.observed_at).toLocaleString()}
              </p>
              {pr.blockers.length > 0 && <p className="mt-1">Blockers: {pr.blockers.join('; ')}</p>}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function RunLink({ url }: { url?: string | null }) {
  if (!url || !safeRunUrl(url)) return null;
  return (
    <a
      className="mt-1 inline-block text-primary hover:underline"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      Open run ↗
    </a>
  );
}

function PullRequestLink({
  repository,
  number,
  url,
}: {
  repository: string;
  number: number;
  url: string;
}) {
  const label = `${repository}#${number}`;
  if (!safeGithubUrl(url)) return <span className="font-medium text-foreground">{label}</span>;
  return (
    <a
      className="font-medium text-primary hover:underline"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {label}
    </a>
  );
}

function safeGithubUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      url.hostname === 'github.com' &&
      url.pathname.startsWith('/the-langston-co/')
    );
  } catch {
    return false;
  }
}

function safeRunUrl(value: string | null | undefined): boolean {
  try {
    const url = new URL(value ?? '');
    return url.protocol === 'https:' && url.hostname === 'agent-runs.thelangstonco.com';
  } catch {
    return false;
  }
}
